import { ITEM_TITLE_KEYS } from './config';
import { CASE_SIDEBAR_SECTIONS, SECTION_TITLE_KEYS } from './config/sections';
import { getAvailableOoPagesForStatus } from './resolve/ooStatusPages';
import { resolveItemSubTitle } from './resolve/itemFields';
import type { BuildBtlCaseSidebarResult, CaseSection, CaseSectionItem } from './types';
import { CaseSidebarItemId, CaseSidebarSection } from './types';
import type { BtlApplication } from 'api/btlApplicationForm/types';
import type { TypedFormatMessageFn } from 'types/intl';

export interface OoCaseSidebarContext {
  status: string;
  hasValuation: boolean;
  hasConveyancing: boolean;
  isNewSectionsEnabled: boolean;
}

export interface BuildOoCaseSidebarParams {
  formatMessage: TypedFormatMessageFn;
  context: OoCaseSidebarContext;
  application?: BtlApplication;
}

function buildSectionItem(
  itemId: CaseSidebarItemId,
  formatMessage: TypedFormatMessageFn,
  application?: BtlApplication,
): CaseSectionItem {
  return {
    id: itemId,
    title: formatMessage({ id: ITEM_TITLE_KEYS[itemId] }),
    subTitle: application ? resolveItemSubTitle(itemId, application) : '',
    isDisabled: false,
    component: null,
    warnings: [],
  };
}

function filterItemsByDataAvailability(
  itemIds: CaseSidebarItemId[],
  context: OoCaseSidebarContext,
): CaseSidebarItemId[] {
  return itemIds.filter(itemId => {
    if (itemId === CaseSidebarItemId.VALUATION) {
      return context.hasValuation;
    }

    if (itemId === CaseSidebarItemId.POST_OFFER) {
      return context.hasConveyancing;
    }

    return true;
  });
}

const OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS = [
  CaseSidebarItemId.FINANCING,
  CaseSidebarItemId.APPLICANTS,
  CaseSidebarItemId.EXPENSES,
];

export const OO_NEW_SECTION_ITEM_IDS: CaseSidebarItemId[] = [
  CaseSidebarItemId.OVERVIEW,
  ...OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS,
  CaseSidebarItemId.PROPERTY,
];

function filterItemsByNewSectionsFlag(
  itemIds: CaseSidebarItemId[],
  context: OoCaseSidebarContext,
): CaseSidebarItemId[] {
  if (context.isNewSectionsEnabled) {
    return itemIds;
  }

  return itemIds.filter(itemId => !OO_NEW_SECTION_ITEM_IDS.includes(itemId));
}

export function getVisibleOoItemIdsForSection(
  section: CaseSidebarSection,
  context: OoCaseSidebarContext,
): CaseSidebarItemId[] {
  const pagesBySection = getAvailableOoPagesForStatus(context.status);

  if (section === CaseSidebarSection.APPLICATION) {
    const applicationItemIds = context.isNewSectionsEnabled
      ? [
          ...OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS,
          ...filterItemsByDataAvailability(pagesBySection[CaseSidebarSection.APPLICATION], context).filter(
            itemId => !OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS.includes(itemId),
          ),
        ]
      : filterItemsByDataAvailability(pagesBySection[CaseSidebarSection.APPLICATION], context);

    return filterItemsByNewSectionsFlag(applicationItemIds, context);
  }

  const sectionItemIds = filterItemsByNewSectionsFlag(
    filterItemsByDataAvailability(pagesBySection[section], context),
    context,
  );

  if (!context.isNewSectionsEnabled && section === CaseSidebarSection.DOCUMENTS) {
    return sectionItemIds.filter(itemId => itemId !== CaseSidebarItemId.CUSTOMER_FILES);
  }

  return sectionItemIds;
}

export function shouldShowOoOverviewNavItem(context: OoCaseSidebarContext): boolean {
  if (context.isNewSectionsEnabled) {
    return true;
  }

  const documentItems = filterItemsByDataAvailability(
    getAvailableOoPagesForStatus(context.status)[CaseSidebarSection.DOCUMENTS],
    context,
  );

  return documentItems.includes(CaseSidebarItemId.CUSTOMER_FILES);
}

function buildOoOverviewNavItem(
  formatMessage: TypedFormatMessageFn,
  context: OoCaseSidebarContext,
  application?: BtlApplication,
): CaseSectionItem {
  if (context.isNewSectionsEnabled) {
    return buildSectionItem(CaseSidebarItemId.OVERVIEW, formatMessage, application);
  }

  return {
    ...buildSectionItem(CaseSidebarItemId.CUSTOMER_FILES, formatMessage, application),
    title: formatMessage({ id: ITEM_TITLE_KEYS[CaseSidebarItemId.OVERVIEW] }),
    iconItemId: CaseSidebarItemId.OVERVIEW,
  };
}

export function resolveOoSectionTitleKey(
  itemId: CaseSidebarItemId,
  context: OoCaseSidebarContext,
): typeof ITEM_TITLE_KEYS[CaseSidebarItemId] {
  if (!context.isNewSectionsEnabled && itemId === CaseSidebarItemId.CUSTOMER_FILES) {
    return ITEM_TITLE_KEYS[CaseSidebarItemId.OVERVIEW];
  }

  return ITEM_TITLE_KEYS[itemId];
}

function buildApplicationSectionItems(
  formatMessage: TypedFormatMessageFn,
  context: OoCaseSidebarContext,
  application?: BtlApplication,
): CaseSectionItem[] {
  return getVisibleOoItemIdsForSection(CaseSidebarSection.APPLICATION, context).map(itemId =>
    buildSectionItem(itemId, formatMessage, application),
  );
}

function buildSectionsFromPages(
  pagesBySection: ReturnType<typeof getAvailableOoPagesForStatus>,
  formatMessage: TypedFormatMessageFn,
  context: OoCaseSidebarContext,
  application?: BtlApplication,
): CaseSection[] {
  const sectionConfigs = [
    { section: CaseSidebarSection.DOCUMENTS, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.DOCUMENTS] },
    { section: CaseSidebarSection.APPLICATION, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.APPLICATION] },
    { section: CaseSidebarSection.OTHER, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.OTHER] },
  ];

  return sectionConfigs.reduce<CaseSection[]>((result, { section, titleKey }) => {
    const children =
      section === CaseSidebarSection.APPLICATION
        ? buildApplicationSectionItems(formatMessage, context, application)
        : getVisibleOoItemIdsForSection(section, context).map(itemId =>
            buildSectionItem(itemId, formatMessage, application),
          );

    if (children.length === 0) {
      return result;
    }

    result.push({
      title: formatMessage({ id: titleKey }),
      children,
    });

    return result;
  }, []);
}

export function getVisibleOoCaseSidebarItemIds(context: OoCaseSidebarContext): CaseSidebarItemId[] {
  const sectionItemIds = CASE_SIDEBAR_SECTIONS.flatMap(section => getVisibleOoItemIdsForSection(section, context));

  if (context.isNewSectionsEnabled) {
    return [CaseSidebarItemId.OVERVIEW, ...sectionItemIds];
  }

  if (shouldShowOoOverviewNavItem(context)) {
    return [CaseSidebarItemId.CUSTOMER_FILES, ...sectionItemIds];
  }

  return sectionItemIds;
}

export function buildOoCaseSidebar(params: BuildOoCaseSidebarParams): BuildBtlCaseSidebarResult {
  const pagesBySection = getAvailableOoPagesForStatus(params.context.status);

  return {
    overviewItem: buildOoOverviewNavItem(params.formatMessage, params.context, params.application),
    state: {
      sections: buildSectionsFromPages(pagesBySection, params.formatMessage, params.context, params.application),
      isTopSlicing: false,
    },
  };
}
